import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OVER_SPEED_LIMIT_KMH } from "../constants/tracking";
import type { RouteCoordinate } from "../types";
import { calculateBearing, haversineDistance } from "../utils/tracking.utils";

export type RoutePlaybackSpeed = 2 | 5 | 10;

export type RoutePlaybackPosition = {
	lat: number;
	lng: number;
	bearing: number;
	speed: number;
	distanceKm: number;
	timestamp: number;
};

type UseRoutePlaybackResult = {
	isPlaying: boolean;
	speed: RoutePlaybackSpeed;
	progress: number;
	elapsedMs: number;
	totalMs: number;
	currentPosition: RoutePlaybackPosition | null;
	overSpeedMarkers: number[];
	toggle: () => void;
	setSpeed: (speed: RoutePlaybackSpeed) => void;
	seekToProgress: (progress: number) => void;
};

function toTimestamp(point: RouteCoordinate): number {
	return point.dateTelemetry ? new Date(point.dateTelemetry).getTime() : 0;
}

const UI_THROTTLE_MS = 50;

export function useRoutePlayback(
	points: RouteCoordinate[]
): UseRoutePlaybackResult {
	const [isPlaying, setIsPlaying] = useState(false);
	const [speed, setSpeed] = useState<RoutePlaybackSpeed>(10);
	const [elapsedMs, setElapsedMs] = useState(0);

	const elapsedMsRef = useRef(0);
	const lastFrameTimeRef = useRef<number | null>(null);
	const lastUiUpdateRef = useRef(0);
	const animationFrameRef = useRef<number | null>(null);
	const isMountedRef = useRef(true);
	const speedRef = useRef(speed);
	const totalMsRef = useRef(0);

	const startTimestamp = points.length > 0 ? toTimestamp(points[0]) : 0;
	const endTimestamp =
		points.length > 0 ? toTimestamp(points.at(-1) as RouteCoordinate) : 0;
	const totalMs = Math.max(0, endTimestamp - startTimestamp);

	totalMsRef.current = totalMs;

	useEffect(() => {
		speedRef.current = speed;
	}, [speed]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset playback whenever a new points array arrives
	useEffect(() => {
		elapsedMsRef.current = 0;
		setElapsedMs(0);
		setIsPlaying(false);
	}, [points]);

	useEffect(() => {
		if (!isPlaying) {
			lastFrameTimeRef.current = null;
			return;
		}

		const tick = (now: number) => {
			if (!isMountedRef.current) {
				return;
			}
			if (lastFrameTimeRef.current === null) {
				lastFrameTimeRef.current = now;
			}
			const deltaMs = now - lastFrameTimeRef.current;
			lastFrameTimeRef.current = now;

			const currentTotalMs = totalMsRef.current;
			const currentSpeed = speedRef.current;
			const next = elapsedMsRef.current + deltaMs * currentSpeed;

			if (next >= currentTotalMs) {
				elapsedMsRef.current = currentTotalMs;
				setElapsedMs(currentTotalMs);
				setIsPlaying(false);
				return;
			}

			elapsedMsRef.current = next;

			if (now - lastUiUpdateRef.current >= UI_THROTTLE_MS) {
				lastUiUpdateRef.current = now;
				setElapsedMs(next);
			}

			animationFrameRef.current = requestAnimationFrame(tick);
		};

		lastUiUpdateRef.current = performance.now();
		animationFrameRef.current = requestAnimationFrame(tick);

		return () => {
			if (animationFrameRef.current !== null) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [isPlaying]);

	const toggle = useCallback(() => {
		setIsPlaying((previous) => {
			if (totalMsRef.current <= 0) {
				return previous;
			}
			if (!previous && elapsedMsRef.current >= totalMsRef.current) {
				elapsedMsRef.current = 0;
				setElapsedMs(0);
			}
			return !previous;
		});
	}, []);

	const seekToProgress = useCallback(
		(progress: number) => {
			const clamped = Math.min(1, Math.max(0, progress));
			const newElapsed = clamped * totalMs;
			elapsedMsRef.current = newElapsed;
			setElapsedMs(newElapsed);
		},
		[totalMs]
	);

	const cumulativeDistancesKm = useMemo<number[]>(() => {
		const distances: number[] = [0];
		for (let i = 1; i < points.length; i++) {
			const segmentKm = haversineDistance(
				points[i - 1].lat,
				points[i - 1].lng,
				points[i].lat,
				points[i].lng
			);
			distances.push(distances[i - 1] + segmentKm);
		}
		return distances;
	}, [points]);

	const currentPosition = useMemo<RoutePlaybackPosition | null>(() => {
		if (points.length === 0) {
			return null;
		}
		if (points.length === 1) {
			return {
				lat: points[0].lat,
				lng: points[0].lng,
				bearing: 0,
				speed: points[0].speed ?? 0,
				distanceKm: 0,
				timestamp: toTimestamp(points[0]),
			};
		}

		const targetTimestamp = startTimestamp + elapsedMs;

		let index = 0;
		for (let i = 0; i < points.length - 1; i++) {
			index = i;
			if (toTimestamp(points[i + 1]) >= targetTimestamp) {
				break;
			}
		}

		const current = points[index];
		const next = points[Math.min(index + 1, points.length - 1)];
		const currentTimestamp = toTimestamp(current);
		const nextTimestamp = toTimestamp(next);
		const segmentMs = nextTimestamp - currentTimestamp;
		const fraction =
			segmentMs > 0
				? Math.min(
						1,
						Math.max(0, (targetTimestamp - currentTimestamp) / segmentMs)
					)
				: 0;
		const segmentKm =
			cumulativeDistancesKm[index + 1] - cumulativeDistancesKm[index];

		return {
			lat: current.lat + (next.lat - current.lat) * fraction,
			lng: current.lng + (next.lng - current.lng) * fraction,
			bearing: calculateBearing(current, next),
			speed:
				(current.speed ?? 0) +
				((next.speed ?? 0) - (current.speed ?? 0)) * fraction,
			distanceKm: cumulativeDistancesKm[index] + segmentKm * fraction,
			timestamp:
				currentTimestamp + (nextTimestamp - currentTimestamp) * fraction,
		};
	}, [points, elapsedMs, startTimestamp, cumulativeDistancesKm]);

	const overSpeedMarkers = useMemo<number[]>(() => {
		if (totalMs <= 0) {
			return [];
		}

		const markers: number[] = [];
		for (let i = 0; i < points.length; i++) {
			const isOverSpeed = (points[i].speed ?? 0) > OVER_SPEED_LIMIT_KMH;
			const wasOverSpeed =
				i > 0 && (points[i - 1].speed ?? 0) > OVER_SPEED_LIMIT_KMH;

			if (isOverSpeed && !wasOverSpeed) {
				markers.push((toTimestamp(points[i]) - startTimestamp) / totalMs);
			}
		}

		return markers;
	}, [points, totalMs, startTimestamp]);

	return {
		isPlaying,
		speed,
		progress: totalMs > 0 ? elapsedMs / totalMs : 0,
		elapsedMs,
		totalMs,
		currentPosition,
		overSpeedMarkers,
		toggle,
		setSpeed,
		seekToProgress,
	};
}
